import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { acquireAudioQueueLock, deleteRecording, getCaseIdFromKey, loadAllPendingRecordings, releaseAudioQueueLock } from '../Utils/audioQueue';
import { executeHttpCommandBridge } from '../Utils/httpCommandBridge';
import { apiRequest, clearApiSessionToken, setApiSessionToken } from '../Utils/apiClient';

const WebSocketContext = createContext();

export const useWebSocket = () => useContext(WebSocketContext);

const createEmptyPermissionScope = () => ({
  projects: { read: [], upload: [], edit: [] },
  collections: { read: [], upload: [], edit: [] },
  storage: { read: false, write: false }
});

const normalizeScopeIdList = (values) => {
  if (!Array.isArray(values)) {
    return [];
  }
  const normalized = [];
  values.forEach((value) => {
    const parsed = Number.parseInt(value, 10);
    if (Number.isInteger(parsed) && parsed > 0 && !normalized.includes(parsed)) {
      normalized.push(parsed);
    }
  });
  return normalized;
};

const normalizeBooleanFlag = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return false;
    }
    if (['1', 'true', 'yes', 'on'].includes(normalized)) {
      return true;
    }
    if (['0', 'false', 'no', 'off'].includes(normalized)) {
      return false;
    }
  }
  return Boolean(value);
};

const AUDIO_UPLOAD_TIMEOUT_MIN_MS = 90 * 1000;
const AUDIO_UPLOAD_TIMEOUT_MAX_MS = 20 * 60 * 1000;
const AUDIO_UPLOAD_TIMEOUT_SIZE_BUCKET_BYTES = 2 * 1024 * 1024;

const getAudioUploadTimeoutMs = (fileSizeBytes = 0) => {
  const safeBytes = Math.max(0, Number(fileSizeBytes) || 0);
  const sizeBuckets = Math.max(0, Math.ceil(safeBytes / AUDIO_UPLOAD_TIMEOUT_SIZE_BUCKET_BYTES));
  const sizePenalty = sizeBuckets * 45 * 1000;
  return Math.min(
    AUDIO_UPLOAD_TIMEOUT_MAX_MS,
    Math.max(AUDIO_UPLOAD_TIMEOUT_MIN_MS, AUDIO_UPLOAD_TIMEOUT_MIN_MS + sizePenalty)
  );
};

const normalizePermissionScope = (rawScope) => {
  const normalized = createEmptyPermissionScope();
  let scope = rawScope;

  if (typeof scope === 'string') {
    try {
      scope = JSON.parse(scope);
    } catch (error) {
      scope = {};
    }
  }

  if (!scope || typeof scope !== 'object') {
    return normalized;
  }

  ['projects', 'collections'].forEach((resource) => {
    const resourceScope = scope[resource];
    if (!resourceScope || typeof resourceScope !== 'object') {
      return;
    }
    normalized[resource].read = normalizeScopeIdList(resourceScope.read);
    normalized[resource].upload = normalizeScopeIdList(resourceScope.upload);
    normalized[resource].edit = normalizeScopeIdList(resourceScope.edit);

    // Keep scope semantics consistent:
    // edit => upload + read
    normalized[resource].edit.forEach((id) => {
      if (!normalized[resource].upload.includes(id)) {
        normalized[resource].upload.push(id);
      }
      if (!normalized[resource].read.includes(id)) {
        normalized[resource].read.push(id);
      }
    });
  });

  const storageScope = scope.storage;
  if (storageScope && typeof storageScope === 'object') {
    normalized.storage.read = normalizeBooleanFlag(storageScope.read);
    normalized.storage.write = normalizeBooleanFlag(storageScope.write);
    if (normalized.storage.write) {
      normalized.storage.read = true;
    }
  }

  return normalized;
};

const hasScopedEditAccess = (scope) => {
  const normalized = normalizePermissionScope(scope);
  return normalized.projects.edit.length > 0 || normalized.collections.edit.length > 0;
};

const hasScopedUploadAccess = (scope) => {
  const normalized = normalizePermissionScope(scope);
  return (
    normalized.projects.upload.length > 0 ||
    normalized.projects.edit.length > 0 ||
    normalized.collections.upload.length > 0 ||
    normalized.collections.edit.length > 0
  );
};

const hasScopedStorageReadAccess = (scope) => {
  const normalized = normalizePermissionScope(scope);
  return Boolean(normalized.storage.read || normalized.storage.write);
};

const hasScopedStorageWriteAccess = (scope) => {
  const normalized = normalizePermissionScope(scope);
  return Boolean(normalized.storage.write);
};

const normalizePermissionValue = (rawPermission) => {
  const permission = String(rawPermission || '').trim().toLowerCase();
  if (!permission) {
    return '';
  }
  if (permission === 'edit') {
    return 'editor';
  }
  if ([
    'read_only',
    'readonly',
    'read-only',
    'read only',
    'viewer',
    'user'
  ].includes(permission)) {
    return 'view_only';
  }
  return permission;
};

const normalizePublicRouteAccess = (raw = {}) => {
  const normalizedLevel = String(
    raw.publicAccessLevel ?? raw.level ?? raw.mode ?? 'none'
  ).trim().toLowerCase();
  const hasExplicitLevel = normalizedLevel !== '' && normalizedLevel !== 'none';
  const canReadFromLevel = normalizedLevel === 'view' || normalizedLevel === 'view_upload' || normalizedLevel === 'read';
  const canUploadFromLevel = normalizedLevel === 'upload' || normalizedLevel === 'view_upload' || normalizedLevel === 'upload_only';
  const canRead = hasExplicitLevel
    ? canReadFromLevel
    : (raw.canRead === true || raw.can_read === true);
  const canUpload = hasExplicitLevel
    ? canUploadFromLevel
    : (raw.canUpload === true || raw.can_upload === true);

  let mode = 'none';
  if (canRead && canUpload) {
    mode = 'read_upload';
  } else if (canUpload) {
    mode = 'upload_only';
  } else if (canRead) {
    mode = 'read';
  }

  return {
    isPublic: Boolean(raw.isPublic ?? raw.is_public ?? mode !== 'none'),
    canRead,
    canUpload,
    mode
  };
};

const isPublicCasePath = (pathname) => String(pathname || '').startsWith('/public/case/');
const PUBLIC_USERNAME = 'public';

const getPublicCaseIdFromPath = (pathname) => {
  const match = String(pathname || '').match(/^\/public\/case\/(\d+)(?:\/|$)/);
  if (!match) {
    return null;
  }
  const parsed = Number.parseInt(match[1], 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const buildPublicPermissionScope = (pathname, access) => {
  const scope = createEmptyPermissionScope();
  const caseId = getPublicCaseIdFromPath(pathname);
  if (!caseId) {
    return scope;
  }
  if (access?.canRead) {
    scope.projects.read = [caseId];
  }
  if (access?.canUpload) {
    scope.projects.upload = [caseId];
  }
  return scope;
};

const buildUserInfo = (raw = {}) => {
  const permissions = normalizePermissionValue(raw.permissions ?? raw.permission);
  const permissionScope = normalizePermissionScope(raw.permission_scope ?? raw.permissionScope);
  const scopedReadOnly = permissions === 'scoped' && !hasScopedEditAccess(permissionScope);
  const scopedUpload = permissions === 'scoped' && hasScopedUploadAccess(permissionScope);
  const scopedStorageRead = permissions === 'scoped' && hasScopedStorageReadAccess(permissionScope);
  const scopedStorageWrite = permissions === 'scoped' && hasScopedStorageWriteAccess(permissionScope);
  const canUpload = permissions === 'admin' || permissions === 'editor' || scopedUpload;
  const canManageStorage = permissions === 'admin' || permissions === 'editor' || scopedStorageRead;
  const canWriteStorage = permissions === 'admin' || permissions === 'editor' || scopedStorageWrite;

  return {
    username: raw.username || '',
    permissions,
    permission_scope: permissionScope,
    get isReadOnly() { return permissions === 'view_only' || scopedReadOnly; },
    get isAdmin() { return permissions === 'admin'; },
    get isEdit() { return permissions === 'editor'; },
    get isScoped() { return permissions === 'scoped'; },
    get hasScopedEdit() { return hasScopedEditAccess(permissionScope); },
    get hasScopedUpload() { return scopedUpload; },
    get hasScopedStorageRead() { return scopedStorageRead; },
    get hasScopedStorageWrite() { return scopedStorageWrite; },
    get hasStorageRead() { return canManageStorage; },
    get hasStorageWrite() { return canWriteStorage; },
    get canManageStorage() { return canManageStorage; },
    get canWriteStorage() { return canWriteStorage; },
    get canUpload() { return canUpload; }
  };
};

// Dedicated hook for accessing user information throughout the app
// This provides a cleaner API and includes computed properties
export const useUser = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useUser must be used within a WebSocketProvider');
  }

  const permissions = normalizePermissionValue(context.userInfo?.permissions ?? context.userInfo?.permission);
  const permissionScope = normalizePermissionScope(
    context.userInfo?.permission_scope ?? context.userInfo?.permissionScope
  );
  const hasScopeEdit = hasScopedEditAccess(permissionScope);
  const hasScopeUpload = hasScopedUploadAccess(permissionScope);
  const hasScopeStorageRead = hasScopedStorageReadAccess(permissionScope);
  const hasScopeStorageWrite = hasScopedStorageWriteAccess(permissionScope);
  const isScopedReadOnly = permissions === 'scoped' && !hasScopeEdit;
  const isPublicRoute = Boolean(context.isPublicRouteActive);
  const publicRouteAccess = normalizePublicRouteAccess(context.publicRouteAccess);
  const publicUsername = isPublicRoute ? PUBLIC_USERNAME : (context.userInfo?.username || '');
  const publicPathname = typeof window !== 'undefined' ? window.location?.pathname : '';
  const isPublicReadOnly = isPublicRoute && publicRouteAccess.canRead && !publicRouteAccess.canUpload;
  const isPublicUploadOnly = isPublicRoute && publicRouteAccess.canUpload && !publicRouteAccess.canRead;
  const isPublicReadUpload = isPublicRoute && publicRouteAccess.canRead && publicRouteAccess.canUpload;
  const effectivePermissionScope = useMemo(
    () => (isPublicRoute ? buildPublicPermissionScope(publicPathname, publicRouteAccess) : permissionScope),
    [isPublicRoute, permissionScope, publicPathname, publicRouteAccess]
  );
  const canManageProject = !isPublicRoute && context.isUserLoggedIn && (
    permissions === 'admin' ||
    permissions === 'editor' ||
    (permissions === 'scoped' && hasScopeEdit)
  );
  const canManageStorage = !isPublicRoute && context.isUserLoggedIn && (
    permissions === 'admin' ||
    permissions === 'editor' ||
    (permissions === 'scoped' && hasScopeStorageRead)
  );
  const canWriteStorage = !isPublicRoute && context.isUserLoggedIn && (
    permissions === 'admin' ||
    permissions === 'editor' ||
    (permissions === 'scoped' && hasScopeStorageWrite)
  );
  const effectivePermissions = (() => {
    if (isPublicRoute) {
      return publicRouteAccess.canUpload ? 'scoped' : 'view_only';
    }
    if (permissions) {
      return permissions;
    }
    if (isPublicReadOnly) {
      return 'view_only';
    }
    if (isPublicUploadOnly || isPublicReadUpload) {
      return 'scoped';
    }
    return permissions;
  })();
  const isReadOnly = isPublicRoute
    ? true
    : permissions === 'view_only' || isScopedReadOnly;
  const canUpload = isPublicRoute
    ? publicRouteAccess.canUpload
    : permissions === 'admin' || permissions === 'editor' || (permissions === 'scoped' && hasScopeUpload);
  const effectiveUserInfo = useMemo(() => ({
    ...(isPublicRoute ? {} : (context.userInfo || {})),
    username: publicUsername,
    permissions: effectivePermissions,
    permission: effectivePermissions,
    permission_scope: effectivePermissionScope,
    permissionScope: effectivePermissionScope,
    isReadOnly,
    hasStorageRead: canManageStorage,
    hasStorageWrite: canWriteStorage,
    canUpload,
    canManageStorage,
    canWriteStorage,
    canManageProject,
    isPublicUser: isPublicRoute
  }), [
    canManageProject,
    canManageStorage,
    canWriteStorage,
    canUpload,
    context.userInfo,
    effectivePermissions,
    effectivePermissionScope,
    isPublicRoute,
    isReadOnly,
    publicUsername
  ]);
  
  return useMemo(() => ({
    username: publicUsername,
    permissions: effectivePermissions,
    rawPermissions: isPublicRoute ? '' : permissions,
    permissionScope: effectivePermissionScope,
    isReadOnly,
    isAdmin: effectivePermissions === 'admin',
    isEdit: effectivePermissions === 'editor',
    isScoped: effectivePermissions === 'scoped',
    hasScopedEdit: effectivePermissions === 'scoped' && !isPublicRoute && hasScopeEdit,
    hasScopedUpload: effectivePermissions === 'scoped' && (isPublicRoute ? publicRouteAccess.canUpload : hasScopeUpload),
    hasScopedStorageRead: effectivePermissions === 'scoped' && !isPublicRoute && hasScopeStorageRead,
    hasScopedStorageWrite: effectivePermissions === 'scoped' && !isPublicRoute && hasScopeStorageWrite,
    hasStorageRead: canManageStorage,
    hasStorageWrite: canWriteStorage,
    canUpload,
    canManageStorage,
    canWriteStorage,
    canManageProject,
    isPublicAccess: isPublicRoute,
    isPublicUser: isPublicRoute,
    publicAccessMode: publicRouteAccess.mode,
    isLoggedIn: isPublicRoute ? false : context.isUserLoggedIn,
    mfaSetupRequired: context.mfaSetupRequired,
    // Include the full userInfo for backward compatibility
    userInfo: effectiveUserInfo
  }), [
    context.userInfo,
    context.isUserLoggedIn,
    context.mfaSetupRequired,
    isPublicRoute,
    publicUsername,
    permissions,
    effectivePermissions,
    effectivePermissionScope,
    isReadOnly,
    hasScopeEdit,
    hasScopeUpload,
    hasScopeStorageRead,
    hasScopeStorageWrite,
    canUpload,
    canManageStorage,
    canWriteStorage,
    canManageProject,
    publicRouteAccess,
    effectiveUserInfo
  ]);
};

// Commands that should NOT have session tokens added automatically.
// Keep this list tight: user-scoped status/data commands must carry identity.
const SYSTEM_COMMANDS = [];

const HTTP_BRIDGED_COMMANDS = new Set([
  'check_login_status',
  'validate_session',
  'get_mfa_status',
  'get_all_users',
  'get_global_mfa_requirement',
  'set_global_mfa_requirement',
  'get_login_activity',
  'getcases',
  'getcase',
  'getWidgets',
  'getDashboardSnapshots',
  'saveDashboardSnapshot',
  'loadDashboardSnapshot',
  'saveWidgetPreferences',
  'saveWidgetOrder',
  'downloadAllProjectUploadFiles',
  'exportexcel_from_variable',
  'exportexcel_all_variables',
  'exportexcel',
  'exportcsv',
  'exportpdf',
  'exportjson',
  'get_project_tags',
  'get_all_unique_tags',
  'get_server_info',
  'get_saved_models',
  'get_text_model_settings',
  'get_vision_model_settings',
  'get_audio_model_settings',
  'get_performance_settings',
  'get_format_settings',
  'get_available_ollama_models',
  'configuration_check',
  'update_configuration',
]);

const getRuntimeDefaultWebSocketUrl = () => {
  if (typeof window === 'undefined' || !window.location) {
    return 'ws://localhost:6789';
  }
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const hostname = String(window.location.hostname || '').trim() || 'localhost';
  return `${protocol}://${hostname}:6789`;
};

const normalizeWebSocketUrl = (input, defaultUrl) => {
  const fallbackUrl = String(defaultUrl || 'ws://localhost:6789').trim() || 'ws://localhost:6789';
  let fallbackHost = 'localhost';
  try {
    const parsedFallback = new URL(fallbackUrl);
    fallbackHost = String(parsedFallback.hostname || '').trim() || 'localhost';
  } catch (error) {
    fallbackHost = 'localhost';
  }

  if (input === undefined || input === null) {
    return fallbackUrl;
  }

  let trimmed = String(input).trim();
  if (!trimmed) {
    return fallbackUrl;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    trimmed = trimmed.replace(/^http/i, 'ws');
  } else if (/^:\d+$/.test(trimmed)) {
    trimmed = `ws://${fallbackHost}${trimmed}`;
  } else if (/^\d+$/.test(trimmed)) {
    trimmed = `ws://${fallbackHost}:${trimmed}`;
  } else if (/^[\w.-]+:\d+$/.test(trimmed)) {
    trimmed = `ws://${trimmed}`;
  } else if (/^[\w.-]+$/.test(trimmed)) {
    trimmed = `ws://${trimmed}:6789`;
  }

  if (!/^wss?:\/\//i.test(trimmed)) {
    return fallbackUrl;
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'ws:' && parsed.protocol !== 'wss:') {
      return fallbackUrl;
    }
    const hostname = String(parsed.hostname || '').trim();
    if (!hostname) {
      return fallbackUrl;
    }
    const hasCustomPath = parsed.pathname && parsed.pathname !== '/';
    const pathname = hasCustomPath ? parsed.pathname : '';
    const search = parsed.search || '';
    return `${parsed.protocol}//${hostname}${parsed.port ? `:${parsed.port}` : ''}${pathname}${search}`;
  } catch (error) {
    return fallbackUrl;
  }
};

export const WebSocketProvider = ({ children }) => {
  const ws = useRef(null);
  // Default WebSocket URL - use same host/ip as website and match protocol
  const DEFAULT_WS_URL = getRuntimeDefaultWebSocketUrl();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoginRequired, setIsLoginRequired] = useState(null); // null = unknown, true/false = determined by server
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem('dataunfold_logged_in');
    return stored === 'true';
  });
  const [isValidatingSession, setIsValidatingSession] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [userInfo, setUserInfo] = useState(() => buildUserInfo());
  const [publicRouteAccess, setPublicRouteAccess] = useState(() => normalizePublicRouteAccess());
  const [isPublicRouteActive, setIsPublicRouteActiveState] = useState(() => isPublicCasePath(typeof window !== 'undefined' ? window.location?.pathname : ''));
  const [mfaSetupRequired, setMfaSetupRequired] = useState(() => {
    return localStorage.getItem('dataunfold_mfa_setup_required') === 'true';
  });
  const [lastCaseCreationUpdate, setLastCaseCreationUpdate] = useState(() => {
    return localStorage.getItem('dataunfold_last_case_creation_update') || '';
  });
  const [lastCaseCreationUpdateAt, setLastCaseCreationUpdateAt] = useState(() => {
    const stored = localStorage.getItem('dataunfold_last_case_creation_update_at');
    const parsed = stored ? Number(stored) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  });
  const messageSubscribers = useRef([]);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 9999999999; // effectively unlimited
  const reconnectInterval = 3000; // 3 seconds
  const lastResumeReconnectAtRef = useRef(0);
  const wasHiddenRef = useRef(typeof document !== 'undefined' ? document.visibilityState !== 'visible' : false);
  const suppressReconnectRef = useRef(false);
  const authTimeoutRef = useRef(null); // Timeout for authentication
  const lastMessageTimeRef = useRef(Date.now());
  const sessionToken = useRef(localStorage.getItem('dataunfold_session_token'));
  const messageQueue = useRef([]); // Queue for messages while session is being validated
  const audioUploadMapRef = useRef(new Map());
  const [storageInfo, setStorageInfo] = useState({
    restrictToUserFolder: false,
    path: ''
  });
  const storageStatusRequestedRef = useRef(false);
  const [serverInfo, setServerInfo] = useState(null);
  // Normalize and validate a server URL or host input
  const normalizeServerUrl = (input) => {
    return normalizeWebSocketUrl(input, DEFAULT_WS_URL);
  };
  // Server URL state + ref (allows switching servers at runtime)
  const [serverUrl, setServerUrlState] = useState(() => {
    const storedUrl = localStorage.getItem('dataunfold_ws_url');
    return normalizeServerUrl(storedUrl || DEFAULT_WS_URL);
  });
  const serverUrlRef = useRef(serverUrl);

  const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error('Failed to read blob'));
      reader.readAsDataURL(blob);
    } catch (error) {
      reject(error);
    }
  });

  // Setter for server URL - normalizes, persists (optional), clears session and reconnects
  const setServerUrl = (newUrl, persist = true) => {
    const normalized = normalizeServerUrl(newUrl);
    serverUrlRef.current = normalized;
    setServerUrlState(normalized);

    try {
      if (persist) {
        localStorage.setItem('dataunfold_ws_url', normalized);
      } else {
        localStorage.removeItem('dataunfold_ws_url');
      }
    } catch (e) {
      console.warn('Failed to persist server URL', e);
    }

    // When switching servers, clear any previous session info - tokens are server-specific
    setIsUserLoggedIn(false);
    setPublicRouteAccess(normalizePublicRouteAccess());
    localStorage.setItem('dataunfold_logged_in', 'false');
    clearApiSessionToken();
    sessionToken.current = null;
    setSessionExpired(false);
    setMfaSetupRequired(false);
    localStorage.removeItem('dataunfold_mfa_setup_required');
    messageQueue.current = [];
    setIsConnected(false);
    setIsValidatingSession(false);
    setIsLoginRequired(null);
    reconnectAttempts.current = 0;
    if (authTimeoutRef.current) {
      clearTimeout(authTimeoutRef.current);
      authTimeoutRef.current = null;
    }

    // Close current socket and reconnect to the new server
    if (ws.current) {
      ws.current.onclose = null; // avoid triggering reconnect loop
      try { ws.current.close(); } catch (e) { /* ignore */ }
      ws.current = null;
    }

    // Re-establish connection using new URL
    connect();
  };

  const clearCustomServer = () => {
    setServerUrl(DEFAULT_WS_URL, false);
  };

  const setPublicRouteActive = useCallback((value) => {
    setIsPublicRouteActiveState(Boolean(value));
  }, []);

  const connect = useCallback(() => {
    suppressReconnectRef.current = false;
    if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
      console.log("WebSocket already connected or connecting.");
      return; // Avoid reconnecting if already connected or in progress
    }

    const urlToUse = normalizeWebSocketUrl(serverUrlRef.current || DEFAULT_WS_URL, DEFAULT_WS_URL);
    if (serverUrlRef.current !== urlToUse) {
      serverUrlRef.current = urlToUse;
      setServerUrlState(urlToUse);
      try {
        localStorage.setItem('dataunfold_ws_url', urlToUse);
      } catch (error) {
        console.warn('Failed to persist normalized server URL', error);
      }
    }

    console.log(`Connecting to WebSocket server at ${urlToUse}`);
    let socket;
    try {
      socket = new WebSocket(urlToUse);
    } catch (error) {
      console.error('Failed to construct WebSocket with URL:', urlToUse, error);
      const fallbackUrl = DEFAULT_WS_URL;
      if (urlToUse !== fallbackUrl) {
        try {
          serverUrlRef.current = fallbackUrl;
          setServerUrlState(fallbackUrl);
          localStorage.setItem('dataunfold_ws_url', fallbackUrl);
        } catch (storageError) {
          console.warn('Failed to persist fallback WebSocket URL', storageError);
        }
        try {
          console.warn(`Retrying WebSocket connection using fallback URL: ${fallbackUrl}`);
          socket = new WebSocket(fallbackUrl);
        } catch (fallbackError) {
          console.error('Failed to construct fallback WebSocket URL:', fallbackUrl, fallbackError);
          setIsConnected(false);
          setIsValidatingSession(false);
          return;
        }
      } else {
        setIsConnected(false);
        setIsValidatingSession(false);
        return;
      }
    }

    // Add a connection timeout to prevent hanging in CONNECTING state
    const connectionTimeout = setTimeout(() => {
      if (socket.readyState === WebSocket.CONNECTING) {
        console.error("WebSocket connection timeout - closing hung connection");
        try { socket.close(); } catch (e) {}
      }
    }, 15000); // 15s timeout for connection

    socket.onopen = () => {
      clearTimeout(connectionTimeout);
      console.log("Connected to the server");
      ws.current = socket;
      setIsConnected(true);
      reconnectAttempts.current = 0;
      lastMessageTimeRef.current = Date.now();
      storageStatusRequestedRef.current = false;
      
      // Request login status when connected
      setTimeout(() => {
        (async () => {
          try {
            const token = sessionToken.current || localStorage.getItem('dataunfold_session_token');
            const isPublicRoute = isPublicCasePath(window.location.pathname);
            if (isPublicRoute) {
              console.log('Public route detected on connection - skipping authentication');
              setIsValidatingSession(false);
              setIsLoginRequired(false);
              setIsUserLoggedIn(false);
              setUserInfo(buildUserInfo({ username: PUBLIC_USERNAME }));
              setPublicRouteAccess(normalizePublicRouteAccess());
              return;
            }

            const status = await apiRequest('/api/v1/auth/status');
            const loginRequired = Boolean(status?.login_required);
            setIsLoginRequired(loginRequired);

            if (token) {
              setIsValidatingSession(true);
              const validation = await apiRequest('/api/v1/auth/validate-session', {
                method: 'POST',
                body: JSON.stringify({ session_token: token })
              });

              if (validation?.success) {
                setIsUserLoggedIn(true);
                setPublicRouteAccess(normalizePublicRouteAccess());
                setSessionExpired(false);
                setMfaSetupRequired(Boolean(validation.mfa_setup_required));
                localStorage.setItem('dataunfold_logged_in', 'true');
                if (validation.mfa_setup_required) {
                  localStorage.setItem('dataunfold_mfa_setup_required', 'true');
                } else {
                  localStorage.removeItem('dataunfold_mfa_setup_required');
                }
                if (validation.username || validation.permissions || validation.permission) {
                  setUserInfo(buildUserInfo(validation));
                }
              } else {
                setIsUserLoggedIn(false);
                setPublicRouteAccess(normalizePublicRouteAccess());
                setSessionExpired(false);
                setMfaSetupRequired(false);
                setUserInfo(buildUserInfo());
                clearApiSessionToken();
                sessionToken.current = null;
                localStorage.setItem('dataunfold_logged_in', 'false');
                localStorage.removeItem('dataunfold_mfa_setup_required');
                if (loginRequired && window.location.pathname !== '/login' && !isPublicRoute) {
                  window.location.href = '/login';
                }
              }
            } else {
              setIsUserLoggedIn(false);
              setPublicRouteAccess(normalizePublicRouteAccess());
              setSessionExpired(false);
              setMfaSetupRequired(false);
              setUserInfo(buildUserInfo());
              localStorage.setItem('dataunfold_logged_in', 'false');
              clearApiSessionToken();
              sessionToken.current = null;
              if (loginRequired && window.location.pathname !== '/login' && !isPublicRoute) {
                window.location.href = '/login';
              }
            }
          } catch (error) {
            console.error('Failed to load auth status via HTTP:', error);
            const token = sessionToken.current || localStorage.getItem('dataunfold_session_token');
            if (token) {
              setSessionExpired(true);
              clearApiSessionToken();
              sessionToken.current = null;
              localStorage.setItem('dataunfold_logged_in', 'false');
              if (window.location.pathname !== '/login' && !isPublicCasePath(window.location.pathname)) {
                window.location.href = '/login';
              }
            } else {
              setIsLoginRequired(false);
              setIsUserLoggedIn(false);
            }
          } finally {
            setIsValidatingSession(false);
          }
        })();
        return;
        if (socket.readyState === WebSocket.OPEN) {
          const token = sessionToken.current || localStorage.getItem('dataunfold_session_token');
          
          // Check if we're on a public route - if so, skip authentication checks
          const isPublicRoute = isPublicCasePath(window.location.pathname);
          
        if (isPublicRoute) {
          console.log('Public route detected on connection - skipping authentication');
          setIsValidatingSession(false);
          setIsLoginRequired(false); // Don't require login for public routes
          setIsUserLoggedIn(false); // Public routes must always behave like anonymous access
          setUserInfo(buildUserInfo({ username: PUBLIC_USERNAME }));
          setPublicRouteAccess(normalizePublicRouteAccess());
            return; // Skip authentication entirely
          }
          
          // Set authentication timeout (30 seconds)
          authTimeoutRef.current = setTimeout(() => {
            console.error('Authentication timeout - stopping validation');
            setIsValidatingSession(false);
            if (socket.readyState !== WebSocket.OPEN) {
              setIsLoginRequired(false); // Assume no login required if server is unreachable
              setIsConnected(false);
            }
          }, 30000);
          
          if (token) {
            // Try to authenticate with stored session token
            setIsValidatingSession(true);
            socket.send(JSON.stringify({
              command: 'validate_session',
              data: { session_token: token }
            }));
          } else {
            // No token, check login status to determine if login is required
            setIsValidatingSession(true); // Set validation state for login status check too
            socket.send(JSON.stringify({
              command: 'check_login_status',
              data: {}
            }));
          }
        }
      
          // Request basic server info for UI gating (platform, capabilities) via HTTP
          executeHttpCommandBridge({ command: 'get_server_info', data: {} })
            .then((bridgedMessage) => {
              if (bridgedMessage) {
                dispatchSyntheticMessage(bridgedMessage);
              }
            })
            .catch((error) => {
              console.error('Failed to load server info via HTTP bridge:', error);
            });
        }, 100); // Small delay to ensure connection is stable
    };    
    socket.onmessage = (event) => {
      lastMessageTimeRef.current = Date.now();
      // Parse JSON message before passing to subscribers
      let parsedMessage;
      try {
        parsedMessage = JSON.parse(event.data);
      } catch (error) {
        console.error("Error parsing message:", error);
        return;
      }

      const isPublicRoute = isPublicCasePath(window.location.pathname);

      if (isPublicRoute && parsedMessage.command === 'getcase') {
        const payload = parsedMessage.data || {};
        const casePayload = payload.case;
        if (casePayload && payload.status !== 'error') {
          setPublicRouteAccess(normalizePublicRouteAccess({
            isPublic: casePayload.isPublic,
            publicAccessLevel: casePayload.publicAccessLevel,
            canRead: payload.can_read,
            canUpload: payload.can_upload,
            mode: payload.access_mode
          }));
        } else {
          setPublicRouteAccess(normalizePublicRouteAccess());
        }
      }

      if (isPublicRoute && parsedMessage.command === 'update_case_public_response') {
        const payload = parsedMessage.data || {};
        if (payload.status === 'success' && payload.isPublic) {
          setPublicRouteAccess(normalizePublicRouteAccess({
            isPublic: payload.isPublic,
            publicAccessLevel: payload.publicAccessLevel
          }));
        } else if (payload.status === 'success') {
          setPublicRouteAccess(normalizePublicRouteAccess());
        }
      }

      if (parsedMessage.command === 'storage_status') {
        const data = parsedMessage.data || {};
        setStorageInfo((prev) => ({
          restrictToUserFolder: Boolean(data.restrict_to_user_folder ?? prev.restrictToUserFolder),
          path: data.path || prev.path || ''
        }));
      }

      if (parsedMessage.command === 'server_info') {
        setServerInfo(parsedMessage.data || {});
      }

  if (parsedMessage.command === 'UpdateCreationCase') {
        let creationActive = false;
        try {
          const raw = sessionStorage.getItem('dataunfold_case_creation_active');
          if (raw) {
            const parsed = JSON.parse(raw);
            const expiresAt = Number(parsed?.expiresAt || 0);
            if (Number.isFinite(expiresAt) && expiresAt > Date.now()) {
              creationActive = true;
            } else {
              sessionStorage.removeItem('dataunfold_case_creation_active');
            }
          }
        } catch (e) {
          // Ignore storage failures (e.g., private mode)
        }

        if (creationActive) {
          const text = parsedMessage?.data?.message ?? parsedMessage?.data?.value ?? '';
          if (text) {
            const now = Date.now();
            setLastCaseCreationUpdate(text);
            setLastCaseCreationUpdateAt(now);
            try {
              localStorage.setItem('dataunfold_last_case_creation_update', text);
              localStorage.setItem('dataunfold_last_case_creation_update_at', String(now));
            } catch (e) {
              // Ignore storage failures (e.g., private mode)
            }
          }
        }
      }

      // Handle login-related messages
      if (parsedMessage.command === 'login_response') {
        // Any login response means the server is reachable; clear auth timeout and validation state.
        if (authTimeoutRef.current) {
          clearTimeout(authTimeoutRef.current);
          authTimeoutRef.current = null;
        }
        setIsValidatingSession(false);
        if (socket.readyState === WebSocket.OPEN) {
          setIsConnected(true);
        }

        if (parsedMessage.data && parsedMessage.data.success) {
          setIsUserLoggedIn(true);
          setPublicRouteAccess(normalizePublicRouteAccess());
          setSessionExpired(false);
          localStorage.setItem('dataunfold_logged_in', 'true');
          const setupRequired = !!parsedMessage.data?.mfa_setup_required;
          setMfaSetupRequired(setupRequired);
          if (setupRequired) {
            localStorage.setItem('dataunfold_mfa_setup_required', 'true');
          } else {
            localStorage.removeItem('dataunfold_mfa_setup_required');
          }
          
          // Store session token if provided
          if (parsedMessage.data.session_token) {
            sessionToken.current = parsedMessage.data.session_token;
            setApiSessionToken(parsedMessage.data.session_token);
          }

          if (parsedMessage.data.username || parsedMessage.data.permissions || parsedMessage.data.permission) {
            setUserInfo(buildUserInfo(parsedMessage.data));
          }

          // Flush any queued messages now that login succeeded.
          const queuedMessages = messageQueue.current;
          messageQueue.current = [];
          if (queuedMessages.length) {
            console.log(`Processing ${queuedMessages.length} queued messages after login`);
            queuedMessages.forEach(message => {
              const isPublicRoute = isPublicCasePath(window.location.pathname);
              if (HTTP_BRIDGED_COMMANDS.has(message.command) && !(isPublicRoute && message.command === 'getcase')) {
                executeHttpCommandBridge(message)
                  .then((bridgedMessage) => {
                    if (bridgedMessage) {
                      dispatchSyntheticMessage(bridgedMessage);
                    }
                  })
                  .catch((error) => {
                    dispatchSyntheticMessage({
                      command: message.command,
                      data: {
                        status: 'error',
                        message: error?.message || 'Request failed'
                      }
                    });
                  });
                return;
              }
              if (socket.readyState === WebSocket.OPEN) {
                const currentToken = sessionToken.current || localStorage.getItem('dataunfold_session_token');
                if (!SYSTEM_COMMANDS.includes(message.command) && currentToken) {
                  message.data = message.data || {};
                  if (!message.data.session_token) {
                    message.data.session_token = currentToken;
                    console.log(`Re-adding session token to queued command ${message.command}`);
                  }
                }
                socket.send(JSON.stringify(message));
              }
            });
          }
          
          console.log('User logged in successfully');
        } else {
          setIsUserLoggedIn(false);
          setPublicRouteAccess(normalizePublicRouteAccess());
          localStorage.setItem('dataunfold_logged_in', 'false');
          clearApiSessionToken();
          sessionToken.current = null;
          setMfaSetupRequired(false);
          localStorage.removeItem('dataunfold_mfa_setup_required');
          console.log('Login failed:', parsedMessage.data?.message || 'Unknown error');
        }
      } else if (parsedMessage.command === 'check_login_status') {
        // Handle the check_login_status response from server
        const loginRequired = parsedMessage.data?.login_required || false;
        const isLogged = parsedMessage.data?.is_logged || false;
        
        // Clear authentication timeout
        if (authTimeoutRef.current) {
          clearTimeout(authTimeoutRef.current);
          authTimeoutRef.current = null;
        }
        
        setIsLoginRequired(loginRequired);
        setIsValidatingSession(false); // Reset validation state
        
        if (isLogged) {
          setIsUserLoggedIn(true);
          setPublicRouteAccess(normalizePublicRouteAccess());
          setSessionExpired(false);
          localStorage.setItem('dataunfold_logged_in', 'true');
          
          // Store user info if available
          if (parsedMessage.data.username && (parsedMessage.data.permissions || parsedMessage.data.permission)) {
            setUserInfo(buildUserInfo(parsedMessage.data));
          }
        } else {
          setIsUserLoggedIn(false);
          setPublicRouteAccess(normalizePublicRouteAccess());
          localStorage.setItem('dataunfold_logged_in', 'false');
          
          // Clear session token if user is not logged in
          clearApiSessionToken();
          sessionToken.current = null;
          setMfaSetupRequired(false);
          localStorage.removeItem('dataunfold_mfa_setup_required');
        }
        
        console.log('Login status check - required:', loginRequired, 'logged_in:', isLogged);
        
        // If login is required and user is not logged in, redirect to login page
        // BUT: Don't redirect if on a public case route
        const isPublicRoute = window.location.pathname.startsWith('/public/case/');
        console.log('Current pathname:', window.location.pathname, 'isPublicRoute:', isPublicRoute);
        if (loginRequired && !isLogged && !isPublicRoute && window.location.pathname !== '/login') {
          console.log('Login required but user not logged in - redirecting to login page');
          window.location.href = '/login';
        } else if (isPublicRoute) {
          console.log('Public route detected, skipping login redirect');
        }
      } else if (parsedMessage.command === 'login_required') {
        // Keep the old handler for backward compatibility
        const required = parsedMessage.data?.required || false;
        const loggedIn = parsedMessage.data?.logged_in || false;
        setIsLoginRequired(required);
        
        if (loggedIn) {
          setIsUserLoggedIn(true);
          setPublicRouteAccess(normalizePublicRouteAccess());
          setSessionExpired(false);
          localStorage.setItem('dataunfold_logged_in', 'true');
        } else {
          setIsUserLoggedIn(false);
          setPublicRouteAccess(normalizePublicRouteAccess());
          localStorage.setItem('dataunfold_logged_in', 'false');
        }
        
        console.log('Login status - required:', required, 'logged_in:', loggedIn);
      } else if (parsedMessage.command === 'mfa_setup_required') {
        setMfaSetupRequired(true);
        localStorage.setItem('dataunfold_mfa_setup_required', 'true');
      } else if (parsedMessage.command === 'get_mfa_status') {
        if (parsedMessage.data?.success) {
          const required = !!parsedMessage.data.required;
          const enabled = !!parsedMessage.data.enabled;
          const setupRequired = required && !enabled;
          setMfaSetupRequired(setupRequired);
          if (setupRequired) {
            localStorage.setItem('dataunfold_mfa_setup_required', 'true');
          } else {
            localStorage.removeItem('dataunfold_mfa_setup_required');
          }
        }
      } else if (parsedMessage.command === 'verify_mfa_setup') {
        if (parsedMessage.data?.success) {
          setMfaSetupRequired(false);
          localStorage.removeItem('dataunfold_mfa_setup_required');
        }
      } else if (parsedMessage.command === 'disable_mfa') {
        if (parsedMessage.data?.success) {
          const msg = (parsedMessage.data?.message || '').toLowerCase();
          if (msg.includes('setup required')) {
            setMfaSetupRequired(true);
            localStorage.setItem('dataunfold_mfa_setup_required', 'true');
          } else {
            setMfaSetupRequired(false);
            localStorage.removeItem('dataunfold_mfa_setup_required');
          }
        }
      } else if (parsedMessage.command === 'set_global_mfa_requirement') {
        if (parsedMessage.data?.success && parsedMessage.data.hasOwnProperty('mfa_setup_required')) {
          const setupRequired = !!parsedMessage.data.mfa_setup_required;
          setMfaSetupRequired(setupRequired);
          if (setupRequired) {
            localStorage.setItem('dataunfold_mfa_setup_required', 'true');
          } else {
            localStorage.removeItem('dataunfold_mfa_setup_required');
          }
        }
      } else if (parsedMessage.command === 'logout') {
        setIsUserLoggedIn(false);
        setPublicRouteAccess(normalizePublicRouteAccess());
        setSessionExpired(false);
        localStorage.setItem('dataunfold_logged_in', 'false');
        clearApiSessionToken();
        sessionToken.current = null;
        setMfaSetupRequired(false);
        localStorage.removeItem('dataunfold_mfa_setup_required');
        console.log('User logged out by server - redirecting to login page');
        // Redirect to login page only if not already there and not on public route
        const isPublicRouteLogout = window.location.pathname.startsWith('/public/case/');
        if (!isPublicRouteLogout && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      } else if (parsedMessage.command === 'session_invalid') {
        // Server says session is invalid, force logout
        setIsUserLoggedIn(false);
        setPublicRouteAccess(normalizePublicRouteAccess());
        setSessionExpired(true);
        localStorage.setItem('dataunfold_logged_in', 'false');
        clearApiSessionToken();
        sessionToken.current = null;
        setMfaSetupRequired(false);
        localStorage.removeItem('dataunfold_mfa_setup_required');
        setIsValidatingSession(false);
        console.log('Session invalidated by server - redirecting to login page');
        // Clear queued messages since session is invalid
        messageQueue.current = [];
        // Redirect to login page only if not already there and not on public route
        const isPublicRouteInvalid = window.location.pathname.startsWith('/public/case/');
        if (!isPublicRouteInvalid && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      } else if (parsedMessage.command === 'session_validated' || parsedMessage.command === 'validate_session') {
        // Handle session validation response
        // Clear authentication timeout
        if (authTimeoutRef.current) {
          clearTimeout(authTimeoutRef.current);
          authTimeoutRef.current = null;
        }
        
        setIsValidatingSession(false);
        
        // Check for both 'valid' and 'success' fields for compatibility
        const isValid = (parsedMessage.data && (parsedMessage.data.valid === true || parsedMessage.data.success === true));
        
        if (isValid) {
          setIsUserLoggedIn(true);
          setPublicRouteAccess(normalizePublicRouteAccess());
          setSessionExpired(false);
          setIsLoginRequired(true); // If session validation was attempted, login is required
          localStorage.setItem('dataunfold_logged_in', 'true');
          
          // Store user info from session validation
          if (parsedMessage.data.username && (parsedMessage.data.permissions || parsedMessage.data.permission)) {
            setUserInfo(buildUserInfo(parsedMessage.data));
          }
          
          console.log('Session validated successfully');
          
          // Process queued messages
          const queuedMessages = messageQueue.current;
          messageQueue.current = [];
          console.log(`Processing ${queuedMessages.length} queued messages`);
          queuedMessages.forEach(message => {
            const isPublicRoute = isPublicCasePath(window.location.pathname);
            if (HTTP_BRIDGED_COMMANDS.has(message.command) && !(isPublicRoute && message.command === 'getcase')) {
              executeHttpCommandBridge(message)
                .then((bridgedMessage) => {
                  if (bridgedMessage) {
                    dispatchSyntheticMessage(bridgedMessage);
                  }
                })
                .catch((error) => {
                  dispatchSyntheticMessage({
                    command: message.command,
                    data: {
                      status: 'error',
                      message: error?.message || 'Request failed'
                    }
                  });
                });
              return;
            }
            if (socket.readyState === WebSocket.OPEN) {
              // Ensure session token is present in queued messages (except system commands)
              const currentToken = sessionToken.current || localStorage.getItem('dataunfold_session_token');
              if (!SYSTEM_COMMANDS.includes(message.command) && currentToken) {
                message.data = message.data || {};
                if (!message.data.session_token) {
                  message.data.session_token = currentToken;
                  console.log(`Re-adding session token to queued command ${message.command}`);
                }
              }
              socket.send(JSON.stringify(message));
            }
          });
        } else {
          setIsUserLoggedIn(false);
          setPublicRouteAccess(normalizePublicRouteAccess());
          setSessionExpired(false);
          setUserInfo(buildUserInfo());
          localStorage.setItem('dataunfold_logged_in', 'false');
          setMfaSetupRequired(false);
          localStorage.removeItem('dataunfold_mfa_setup_required');
          
          // Only redirect if there was a session token (user was previously logged in)
          const hadSessionToken = sessionToken.current || localStorage.getItem('dataunfold_session_token');
          
          clearApiSessionToken();
          sessionToken.current = null;
          console.log('Session validation failed:', parsedMessage.data?.message);
          // Clear queued messages since validation failed
          messageQueue.current = [];
          
          // Set login required to true for failed validation
          setIsLoginRequired(true);
          if (hadSessionToken) {
            const sessionMessage = (parsedMessage.data?.message || '').toLowerCase();
            const isExpired = sessionMessage.includes('expired') || sessionMessage.includes('invalid');
            if (isExpired) {
              setSessionExpired(true);
              const isPublicRouteExpired = window.location.pathname.startsWith('/public/case/');
              if (!isPublicRouteExpired && window.location.pathname !== '/login') {
                console.log('Session expired - redirecting to login page');
                window.location.href = '/login';
              }
            }
          }
          
          if (hadSessionToken) {
            console.log('Session invalid, checking login requirements...');
          }
          executeHttpCommandBridge({ command: 'check_login_status', data: {} })
            .then((bridgedMessage) => {
              if (bridgedMessage) {
                dispatchSyntheticMessage(bridgedMessage);
              }
            })
            .catch((error) => {
              dispatchSyntheticMessage({
                command: 'check_login_status',
                data: {
                  login_required: true,
                  is_logged: false,
                  message: error?.message || 'Failed to check login status',
                },
              });
            });
        }
      }

      // Pass the parsed JSON directly to subscribers instead of the raw event
      messageSubscribers.current.forEach((callback) => callback(parsedMessage));
    };

    socket.onclose = () => {
      clearTimeout(connectionTimeout);
      console.log("Disconnected from the server");
    setIsConnected(false);
    setIsValidatingSession(false);
    setStorageInfo({ restrictToUserFolder: false, path: '' });
      
      // Clear authentication timeout
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = null;
      }
      
      if (suppressReconnectRef.current) {
        console.log("WebSocket closed intentionally - skipping reconnect.");
        suppressReconnectRef.current = false;
        reconnectAttempts.current = 0;
        return;
      }

      if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current += 1;
        setTimeout(() => {
          connect();
        }, reconnectInterval);
      } else {
        console.error("Max reconnect attempts reached. Could not reconnect to the server.");
        // Fallback to offline mode - assume no login required
        setIsLoginRequired(false);
        setIsUserLoggedIn(false);
        setPublicRouteAccess(normalizePublicRouteAccess());
      }
    };

    socket.onerror = (error) => {
      clearTimeout(connectionTimeout);
      console.error("WebSocket error:", error);
      setIsConnected(false);
      setIsValidatingSession(false);
      
      // Clear authentication timeout
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = null;
      }
      
      // If this is the initial connection attempt and it fails, fallback to offline mode
      // Only do this if we don't have a saved session, to avoid logging out active users on transient errors
      const hasToken = localStorage.getItem('dataunfold_session_token');
      if (reconnectAttempts.current === 0 && !hasToken) {
        console.log('Initial connection failed, falling back to offline mode');
        setIsLoginRequired(false);
        setIsUserLoggedIn(false);
        setPublicRouteAccess(normalizePublicRouteAccess());
      }
    };
  }, [DEFAULT_WS_URL]); // useCallback dependency array

  useEffect(() => {
    // Only auto-connect on mount if we already have a session token
    // or if we're on a public route that needs to fetch public data.
    const hasToken = sessionToken.current || localStorage.getItem('dataunfold_session_token');
    const isPublicRoute = window.location.pathname.startsWith('/public/case/');

    if (hasToken || isPublicRoute) {
      connect(); // Establish WebSocket connection
    } else {
      // No token & not public - avoid connecting by default. Require login.
      setIsValidatingSession(false);
      setIsConnected(false);
      setIsUserLoggedIn(false);
      setPublicRouteAccess(normalizePublicRouteAccess());
      setIsLoginRequired(true);
    }

    return () => {
      // Clear authentication timeout
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = null;
      }
      
      if (ws.current) {
        ws.current.onclose = null; // Prevent triggering reconnection during cleanup
        ws.current.close(); // Close the WebSocket connection when the component unmounts
      }
      messageSubscribers.current = []; // Clear message subscribers
      messageQueue.current = []; // Clear message queue
    };
  }, [connect]); // connect is now stable due to useCallback

  // Mobile/Idle recovery: reconnect only when tab transitions from hidden -> visible.
  // Using `focus` in addition to `visibilitychange` can trigger duplicate reconnects on alt-tab.
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';

      if (!isVisible) {
        wasHiddenRef.current = true;
        return;
      }

      const resumedFromHidden = wasHiddenRef.current;
      wasHiddenRef.current = false;
      if (!resumedFromHidden) {
        return;
      }

      const now = Date.now();
      // Prevent back-to-back reconnects on browsers that dispatch resume events in bursts.
      if (now - lastResumeReconnectAtRef.current < 5000) {
        return;
      }

      const timeSinceLastMessage = now - lastMessageTimeRef.current;
      const hasToken = sessionToken.current || localStorage.getItem('dataunfold_session_token');
      const isPublicRoute = window.location.pathname.startsWith('/public/case/');
      const socketReady = ws.current && ws.current.readyState === WebSocket.OPEN;
      
      // Resume should only reconnect when socket is truly disconnected.
      // Forcing reconnects on temporary "silence" causes auth churn and UI resets.
      if (!isConnected || !socketReady) {
        if (hasToken || isPublicRoute) {
          lastResumeReconnectAtRef.current = now;
          console.log(`App resumed (timeSinceLastMessage: ${timeSinceLastMessage}ms). Reconnecting (socket not ready)...`);
          
          if (ws.current) {
            try {
              // Remove onclose to prevent triggering the built-in delay if we want to reconnect immediately
              ws.current.onclose = null;
              ws.current.close();
            } catch (e) {}
            ws.current = null;
          }
          
          setIsConnected(false);
          reconnectAttempts.current = 0; // Reset attempts to try immediately
          connect();
        }
      } else {
        console.log('App resumed, connection still healthy.');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [connect, isConnected]);

  const subscribeToMessages = useCallback((commands, callback) => {
    // Check if we're getting old-style single callback or new-style filtered callback
    if (typeof commands === 'function') {
      // Old style: single callback for all messages
      const actualCallback = commands;
      messageSubscribers.current.push(actualCallback);
      return () => {
        messageSubscribers.current = messageSubscribers.current.filter((cb) => cb !== actualCallback);
      };    } else if (Array.isArray(commands) && typeof callback === 'function') {
      // New style: filtered callback for specific commands
      const wrappedCallback = (message) => {
        if (message && message.command && commands.includes(message.command)) {
          // Validate that message.data is an object before passing to callback
          if (message.data && typeof message.data === 'object') {
            callback(message);
          } else if (message.data === undefined || message.data === null) {
            // Create a standardized data object if missing
            message.data = {};
            callback(message);
          } else {
            // Handle non-object data by wrapping it in an object
            const wrappedMessage = {
              ...message,
              data: { value: message.data }
            };
            callback(wrappedMessage);
          }
        }
      };
      
      messageSubscribers.current.push(wrappedCallback);
      return () => {
        messageSubscribers.current = messageSubscribers.current.filter((cb) => cb !== wrappedCallback);
      };
    } else {
      console.error('subscribeToMessages called with invalid parameters');
      return () => {}; // Return empty function as a fallback
    }
  }, []);

  const dispatchSyntheticMessage = useCallback((parsedMessage) => {
    const isPublicRoute = isPublicCasePath(window.location.pathname);

    if (isPublicRoute && parsedMessage.command === 'getcase') {
      const payload = parsedMessage.data || {};
      const casePayload = payload.case;
      if (casePayload && payload.status !== 'error') {
        setPublicRouteAccess(normalizePublicRouteAccess({
          isPublic: casePayload.isPublic,
          publicAccessLevel: casePayload.publicAccessLevel,
          canRead: payload.can_read,
          canUpload: payload.can_upload,
          mode: payload.access_mode
        }));
      } else {
        setPublicRouteAccess(normalizePublicRouteAccess());
      }
    }

    if (parsedMessage.command === 'server_info') {
      setServerInfo(parsedMessage.data || {});
    }

    messageSubscribers.current.forEach((subscriber) => {
      try {
        subscriber(parsedMessage);
      } catch (error) {
        console.error('Error dispatching synthetic message:', error);
      }
    });
  }, []);

  // Enhanced sendMessage function that handles session validation queuing
  const sendMessage = useCallback((messageToSend) => {
    // Handle different message formats
    if (typeof messageToSend === 'string') {
      try {
        messageToSend = JSON.parse(messageToSend);
      } catch (e) {
        console.error('Failed to parse message:', e);
        return;
      }
    }

    // Always ensure we have the latest session token
    const currentToken = sessionToken.current || localStorage.getItem('dataunfold_session_token');
    const isPublicRoute = isPublicRouteActive || isPublicCasePath(window.location.pathname);
    
    // Add session token to all commands except system commands
    if (!SYSTEM_COMMANDS.includes(messageToSend.command)) {
      if (isPublicRoute) {
        if (messageToSend.data?.session_token) {
          delete messageToSend.data.session_token;
        }
      } else if (isValidatingSession && currentToken) {
        // Ensure message has session token before queuing
        messageToSend.data = messageToSend.data || {};
        if (!messageToSend.data.session_token) {
          messageToSend.data.session_token = currentToken;
        }
        
        // Queue the message until session validation completes
        messageQueue.current.push(messageToSend);
        console.log(`Queuing command ${messageToSend.command} until session validation completes`);
        return;
      }
      
      // Always add session token to commands if available
      if (!isPublicRoute && currentToken && (!messageToSend.data || !messageToSend.data.session_token)) {
        messageToSend.data = messageToSend.data || {};
        messageToSend.data.session_token = currentToken;
        console.log(`Adding session token to command ${messageToSend.command}`);
      } else if (!isPublicRoute && !currentToken) {
        // Only warn about missing token if not on a public route
        console.warn(`No session token available for command: ${messageToSend.command}`);
      }
    }
    
    // Public project pages must use the websocket path for `getcase` so the
    // backend can hydrate the anonymous public user context before access checks.
    if (HTTP_BRIDGED_COMMANDS.has(messageToSend.command) && !(isPublicRoute && messageToSend.command === 'getcase')) {
      executeHttpCommandBridge(messageToSend)
        .then((bridgedMessage) => {
          if (!bridgedMessage) {
            return;
          }
          dispatchSyntheticMessage(bridgedMessage);
        })
        .catch((error) => {
          dispatchSyntheticMessage({
            command: messageToSend.command,
            data: {
              status: 'error',
              message: error?.message || 'Request failed'
            }
          });
        });
      return;
    }

    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket is not connected');
      return;
    }

    ws.current.send(JSON.stringify(messageToSend));
  }, [dispatchSyntheticMessage, isPublicRouteActive, isValidatingSession]);

  useEffect(() => {
    const currentToken = sessionToken.current || localStorage.getItem('dataunfold_session_token');

    if (isPublicRouteActive) {
      setIsLoginRequired(false);
      setIsValidatingSession(false);
      setIsUserLoggedIn(false);
      setUserInfo(buildUserInfo({ username: PUBLIC_USERNAME }));
      setPublicRouteAccess(normalizePublicRouteAccess());
    }

    const hasActiveSocket = ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING);
    if (!hasActiveSocket) {
      if (isPublicRouteActive || currentToken) {
        reconnectAttempts.current = 0;
        connect();
      }
      return;
    }

    const shouldReconnectForPublicRoute = isPublicRouteActive && (Boolean(currentToken) || isUserLoggedIn);
    const shouldReconnectForPrivateRoute = !isPublicRouteActive && !isUserLoggedIn && Boolean(currentToken);

    if (!shouldReconnectForPublicRoute && !shouldReconnectForPrivateRoute) {
      return;
    }

    ws.current.onclose = null;
    try {
      ws.current.close(1000, isPublicRouteActive ? 'Switching to public route mode' : 'Restoring authenticated route mode');
    } catch (error) {
      console.warn('Failed to close websocket during route mode switch', error);
    }
    ws.current = null;
    setIsConnected(false);
    reconnectAttempts.current = 0;
    connect();
  }, [connect, isPublicRouteActive, isUserLoggedIn]);

  const drainAudioUploadQueue = useCallback(async () => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) return;
    if (!isConnected || isValidatingSession) return;
    if (isLoginRequired === true && !isUserLoggedIn) return;

    if (!acquireAudioQueueLock()) return;

    try {
      const pendingRecords = await loadAllPendingRecordings();
      for (const record of pendingRecords) {
        if (!ws.current || ws.current.readyState !== WebSocket.OPEN) break;
        if (!record?.blob) continue;

        const caseIdValue = getCaseIdFromKey(record.caseId);
        if (!caseIdValue) continue;

        const filename = record.name || `recording-${new Date(record.createdAt || Date.now()).toISOString().replace(/[:.]/g, '-')}.webm`;
        const mimeType = record.mimeType || record.blob.type || 'audio/webm';
        let filedata;

        try {
          filedata = await blobToDataUrl(record.blob);
        } catch (error) {
          console.warn('[WebSocketContext] Failed to read queued audio blob', error);
          continue;
        }

        const ackPromise = new Promise((resolve) => {
          audioUploadMapRef.current.set(filename, { key: record.key, resolve });
        });

        sendMessage({
          command: 'uploadfile',
          caseid: caseIdValue,
          files: [{ filename, filedata }]
        });

        const result = await new Promise((resolve) => {
          let settled = false;
          const timeoutId = setTimeout(() => {
            if (settled) return;
            settled = true;
            audioUploadMapRef.current.delete(filename);
            resolve({ status: 'timeout' });
          }, getAudioUploadTimeoutMs(record.blob?.size));

          ackPromise.then((payload) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            resolve(payload);
          });
        });

        if (result?.status === 'error' || result?.status === 'timeout') {
          break;
        }
      }
    } catch (error) {
      console.warn('[WebSocketContext] Failed to drain audio upload queue', error);
    } finally {
      releaseAudioQueueLock();
    }
  }, [isConnected, isLoginRequired, isUserLoggedIn, isValidatingSession, sendMessage]);

  // Logout function
  const logout = useCallback(() => {
    suppressReconnectRef.current = true;
    reconnectAttempts.current = 0;
    setIsConnected(false);
    setIsValidatingSession(false);
    setIsLoginRequired(true);
    setStorageInfo({ restrictToUserFolder: false, path: '' });
    storageStatusRequestedRef.current = false;

    if (authTimeoutRef.current) {
      clearTimeout(authTimeoutRef.current);
      authTimeoutRef.current = null;
    }

    const currentToken = sessionToken.current || localStorage.getItem('dataunfold_session_token');
    if (currentToken) {
      apiRequest('/api/v1/auth/logout', { method: 'POST' }).catch((error) => {
        console.warn('Failed to logout via HTTP', error);
      });
    }
    
    if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
      try {
        ws.current.close(1000, 'User logout');
      } catch (e) {
        console.warn('Failed to close websocket on logout', e);
      }
    }
    ws.current = null;
    
    // Clear local state
    setIsUserLoggedIn(false);
    setUserInfo(buildUserInfo());
    setPublicRouteAccess(normalizePublicRouteAccess());
    setSessionExpired(false);
    setMfaSetupRequired(false);
    localStorage.setItem('dataunfold_logged_in', 'false');
    clearApiSessionToken();
    localStorage.removeItem('dataunfold_mfa_setup_required');
    localStorage.removeItem('dataunfold_last_case_creation_update');
    localStorage.removeItem('dataunfold_last_case_creation_update_at');
    sessionToken.current = null;
    setLastCaseCreationUpdate('');
    setLastCaseCreationUpdateAt(null);
    
    // Clear message queue
    messageQueue.current = [];
    
    console.log('User logged out - redirecting to login page');
    
    // Redirect to login page only if not already there
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }, [sendMessage]);

  useEffect(() => {
    if (!isConnected || isValidatingSession) return;
    if (isLoginRequired === true && !isUserLoggedIn) return;
    drainAudioUploadQueue();
  }, [drainAudioUploadQueue, isConnected, isLoginRequired, isUserLoggedIn, isValidatingSession]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages(['uploadfile'], (message) => {
      const { status, files, failed } = message.data || {};
      if (!Array.isArray(files) || files.length === 0) return;

      const failedSet = new Set(Array.isArray(failed) ? failed : []);
      files.forEach((filename) => {
        const entry = audioUploadMapRef.current.get(filename);
        if (!entry) return;

        audioUploadMapRef.current.delete(filename);
        const success = status !== 'error' && !failedSet.has(filename);
        if (success && entry.key) {
          deleteRecording(entry.key).catch((error) => {
            console.warn('[WebSocketContext] Failed to remove queued recording', error);
          });
        }

        if (typeof entry.resolve === 'function') {
          entry.resolve({ status, success });
        }
      });

      if (audioUploadMapRef.current.size === 0) {
        drainAudioUploadQueue();
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [drainAudioUploadQueue, subscribeToMessages]);

  return (
    <WebSocketContext.Provider value={{ 
      ws: ws.current, 
      isConnected, 
      subscribeToMessages,
      isLoginRequired,
      isUserLoggedIn,
      isValidatingSession,
      sessionExpired,
      userInfo,
      publicRouteAccess,
      isPublicRouteActive,
      serverInfo,
      mfaSetupRequired,
      lastCaseCreationUpdate,
      lastCaseCreationUpdateAt,
      serverUrl: serverUrlRef.current,
      setServerUrl, // Allows switching to a custom server
      clearCustomServer,
      connect,
      setPublicRouteActive,
      setIsUserLoggedIn, // Allow manual control if needed
      sendMessage, // New method for sending messages with auto session token
      storageInfo,
      logout // Logout function
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};
